import React, { useState } from 'react';
import { Finding } from '../../types/contractAnalyzer';

interface FindingsViewProps {
  findings: Finding[];
  onChipClick?: (type: 'severity' | 'category' | 'jurisdiction', value: string) => void;
}

const severityColor: Record<Finding['severity'], string> = {
  low: 'bg-gray-100 text-gray-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

const severityDescription: Record<Finding['severity'], string> = {
  low: 'Low: minor issue or improvement; consider addressing if convenient',
  medium: 'Medium: notable risk or gap; address during negotiation',
  high: 'High: material risk; address before signing',
  critical: 'Critical: severe risk; requires immediate change or legal review',
};

const FindingsView: React.FC<FindingsViewProps> = ({ findings, onChipClick }) => {
  if (!findings || findings.length === 0) {
    return null;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Findings</h2>
        <span className="text-sm text-gray-500">{findings.length} item(s)</span>
      </div>

      <ul className="space-y-4">
        {findings.map((f) => (
          <FindingItem key={f.id} f={f} onChipClick={onChipClick} />
        ))}
      </ul>
    </div>
  );
};

const FindingItem: React.FC<{ f: Finding; onChipClick?: (type: 'severity' | 'category' | 'jurisdiction', value: string) => void }>
  = ({ f, onChipClick }) => {
  const [expanded, setExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const isAmendments = /schedule of amendments|amendments to jct/i.test(f.title) || /schedule of amendments|amendments to jct/i.test(f.summary);
  return (
    <>
    <li className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onChipClick && onChipClick('severity', f.severity)}
            className={`px-2 py-0.5 rounded-full text-xs font-medium ${severityColor[f.severity]} hover:opacity-80`}
            title={severityDescription[f.severity]}
          >
            {f.severity.toUpperCase()}
          </button>
          <button
            type="button"
            onClick={() => onChipClick && onChipClick('category', f.category)}
            className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 hover:bg-gray-200"
            title="Filter by category"
          >
            {f.category}
          </button>
          <button
            type="button"
            onClick={() => onChipClick && onChipClick('jurisdiction', f.jurisdiction)}
            className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100"
            title="Filter by jurisdiction"
          >
            {f.jurisdiction}
          </button>
        </div>
        <div className="flex items-center gap-2">
          {isAmendments && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" title="Schedule of Amendments impact">
              Amendments
            </span>
          )}
          <span className="text-xs text-gray-500">Confidence: {(f.confidence?.score ?? 0).toFixed(2)}</span>
        </div>
      </div>

      <h3 className="mt-3 text-lg font-semibold text-gray-900">{f.title}</h3>
      <p className="mt-1 text-gray-700">{f.summary}</p>
      {f.confidence?.rationale && (
        <p className="mt-1 text-xs text-gray-500">Why flagged: {f.confidence.rationale}</p>
      )}

      <div className="mt-3">
        <div className="text-xs text-gray-500 mb-1">Clause excerpt</div>
        <pre className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm whitespace-pre-wrap max-h-40 overflow-auto">
          {expanded ? (f.clauseSpan?.text || '') : (f.clauseSpan?.text || '').slice(0, 500)}
          {(!expanded && (f.clauseSpan?.text || '').length > 500) ? '…' : ''}
        </pre>
        {(f.clauseSpan?.text || '').length > 500 && (
          <button
            type="button"
            className="mt-2 text-xs text-blue-600 hover:underline"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}
        <button
          type="button"
          className="mt-2 ml-3 text-xs text-gray-700 hover:underline"
          onClick={() => setShowModal(true)}
        >
          View full clause
        </button>
      </div>

      <div className="mt-3">
        <div className="text-xs text-gray-500 mb-1">Recommendation</div>
        <p className="text-gray-800">{f.recommendation}</p>
        <button
          type="button"
          className="mt-2 text-xs text-blue-600 hover:underline"
          onClick={() => navigator.clipboard?.writeText(f.recommendation || '')}
        >
          Copy recommendation
        </button>
      </div>
    </li>
    {/* Simple modal for full clause */}
    {showModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowModal(false)}>
        <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <h4 className="font-semibold text-gray-900">Clause excerpt</h4>
            <div className="flex items-center gap-2">
              <button
                className="text-blue-600 hover:text-blue-800 text-sm"
                onClick={() => {
                  const text = f.clauseSpan?.text || '';
                  navigator.clipboard?.writeText(text);
                }}
              >
                Copy clause
              </button>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setShowModal(false)}>Close</button>
            </div>
          </div>
          <div className="p-4">
            <pre className="whitespace-pre-wrap text-sm">{f.clauseSpan?.text || ''}</pre>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default FindingsView;
